// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Mail, User, Clock, Eye, EyeOff, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { apiRequest } from '../../services/api/axiosConfig';
import { isValidInvitationToken } from '../../services/api/organizationService';
import firebaseService from '../../services/firebase/firebaseService';
import Image from '../../components/common/Image';
import { applyAuthResult } from '../../utils/applyAuthResult';
import { getPostAuthDestination } from '../../utils/authDestination';

const inputClass =
  'w-full bg-warm-surface border-none rounded-lg py-2.5 px-3 text-sm text-dark placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 transition';

/**
 * verify が 400 で返す reason ごとの文言。サーバ側のメッセージに依存せず
 * ここで出し分ける（不明な reason のときだけサーバのメッセージにフォールバック）。
 */
const VERIFY_REASON_MESSAGES: Record<string, string> = {
  expired: 'この招待は期限切れです。招待者に再送を依頼してください。',
  revoked: 'この招待は取り消されています。招待者にご確認ください。',
  not_found: 'この招待は無効です。URL が正しいかご確認ください。',
};

/** 承認済みの招待を開いたときにログイン画面へ送るまでの待ち時間 */
const ACCEPTED_REDIRECT_DELAY_MS = 3000;

const InviteAcceptPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState<any>(null);
  // 承認済みの招待を踏んだ場合（既にメンバーになっている）
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formErrors, setFormErrors] = useState<{ password?: string; passwordConfirmation?: string }>({});

  useEffect(() => {
    const verifyInvitation = async () => {
      if (!token) {
        setError('招待トークンが見つかりません');
        return;
      }
      // URL由来の値なので、発行形式でなければ API を呼ばずに無効扱いにする
      if (!isValidInvitationToken(token)) {
        setError(VERIFY_REASON_MESSAGES.not_found);
        return;
      }
      setIsLoading(true);
      try {
        const data: any = await apiRequest.get(`organizations/invite/${encodeURIComponent(token)}/verify`);
        if (data?.status === 'accepted') {
          // 既に承認済み。パスワード設定フォームを出しても通らないのでログインへ送る。
          setAlreadyAccepted(true);
          setError(null);
        } else if (data) {
          setInvitation(data);
          setError(null);
        } else {
          setError('招待情報の取得に失敗しました');
        }
      } catch (err: any) {
        const reason = err.response?.data?.reason;
        setError(
          (reason && VERIFY_REASON_MESSAGES[reason]) ||
            err.response?.data?.message ||
            '招待の検証に失敗しました'
        );
      } finally {
        setIsLoading(false);
      }
    };
    verifyInvitation();
  }, [token]);

  // 承認済みの招待はログイン画面へ自動遷移する（すぐ行けるボタンも出す）
  useEffect(() => {
    if (!alreadyAccepted) return;
    const timer = setTimeout(() => navigate('/login'), ACCEPTED_REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [alreadyAccepted, navigate]);

  const validateForm = () => {
    const errors: typeof formErrors = {};
    if (!password) errors.password = 'パスワードを入力してください';
    else if (password.length < 8) errors.password = 'パスワードは8文字以上にしてください';
    if (!passwordConfirmation) errors.passwordConfirmation = '確認用パスワードを入力してください';
    else if (password !== passwordConfirmation) errors.passwordConfirmation = 'パスワードが一致しません';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !isValidInvitationToken(token) || !invitation) return;
    setIsLoading(true);
    setError(null);
    try {
      await apiRequest.post(`organizations/invite/${encodeURIComponent(token)}/accept`, {
        password,
        password_confirmation: passwordConfirmation,
      });
      const loginResult = await firebaseService.loginWithEmailAndPassword(
        invitation.invitation.email,
        password
      );
      if (loginResult && loginResult.user && loginResult.token) {
        // login thunk に渡すと email/password 無しで POST auth/login が飛んで必ず 422 になる。
        // Firebase 経由で既にバックエンド認証済みなので、トークンとユーザーを直接入れる。
        applyAuthResult(dispatch, { token: loginResult.token, user: loginResult.user });
        navigate(getPostAuthDestination(loginResult.user));
      } else {
        setError('ログイン情報の取得に失敗しました');
      }
    } catch (err: any) {
      if (err.response?.data?.message) setError(err.response.data.message);
      else if (err.code === 'auth/user-not-found') setError('アカウントが作成されていません。しばらく待ってから再度お試しください。');
      else if (err.code === 'auth/wrong-password') setError('パスワードが正しくありません。');
      else if (err.code === 'auth/too-many-requests') setError('ログイン試行回数が上限に達しました。しばらく時間をおいてから再度お試しください。');
      else setError(err.message || '招待の承認に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state (initial verify)
  if (isLoading && !invitation) {
    return (
      <div className="bg-warm-bg min-h-screen flex items-center justify-center px-4 py-8 font-sans">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">招待情報を読み込んでいます...</p>
        </div>
      </div>
    );
  }

  // 承認済みの招待
  if (alreadyAccepted) {
    return (
      <div className="bg-warm-bg min-h-screen flex items-center justify-center px-4 py-8 font-sans">
        <div className="w-full max-w-md bg-white rounded-xl p-8 sm:p-10 shadow-sm">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-xl font-semibold text-dark mb-2">招待は承認済みです</h1>
          </div>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full bg-primary hover:bg-primary-hover text-white font-medium rounded-lg py-2.5 text-sm transition-colors"
          >
            ログインページへ
          </button>
        </div>
      </div>
    );
  }

  // Error state (no invitation)
  if (error && !invitation) {
    return (
      <div className="bg-warm-bg min-h-screen flex items-center justify-center px-4 py-8 font-sans">
        <div className="w-full max-w-md bg-white rounded-xl p-8 sm:p-10 shadow-sm">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-7 h-7 text-destructive" />
            </div>
            <h1 className="text-xl font-semibold text-dark mb-2">この招待は使えません</h1>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full bg-primary hover:bg-primary-hover text-white font-medium rounded-lg py-2.5 text-sm transition-colors"
          >
            ログインページへ
          </button>
        </div>
      </div>
    );
  }

  const inv = invitation;
  const expiresAt = inv?.invitation?.expires_at
    ? new Date(inv.invitation.expires_at).toLocaleString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) + 'まで'
    : '';

  return (
    <div className="bg-warm-bg min-h-screen flex items-center justify-center px-4 py-8 font-sans">
      <div className="w-full max-w-md bg-white rounded-xl p-8 sm:p-10 shadow-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <Image category="common" fileName="logo.png" alt="ロゴ" className="h-10 w-auto" />
          </div>
          <p className="text-sm text-gray-500">広告クリエイティブAIアシスタント</p>
        </div>

        <div className="mb-6 text-center">
          <h1 className="text-lg font-semibold text-dark">組織への招待</h1>
          <p className="text-sm text-gray-500 mt-1">パスワードを設定してアカウントを作成してください</p>
        </div>

        {/* Invitation info panel */}
        {inv && (
          <div className="bg-warm-surface rounded-xl p-4 mb-6 divide-y divide-warm-border">
            <div className="flex items-center gap-3 pb-3">
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4 text-violet-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-400 leading-none mb-0.5">招待された組織</p>
                <p className="text-sm font-semibold text-dark truncate">{inv.organization?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 py-3">
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-400 leading-none mb-0.5">招待されたメールアドレス</p>
                <p className="text-sm text-dark truncate">{inv.invitation?.email}</p>
              </div>
            </div>
            {inv.inviter && (
              <div className="flex items-center gap-3 py-3">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-rose-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-400 leading-none mb-0.5">招待者</p>
                  <p className="text-sm text-dark truncate">{inv.inviter.name}</p>
                </div>
              </div>
            )}
            {expiresAt && (
              <div className="flex items-center gap-3 pt-3">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-amber-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-400 leading-none mb-0.5">招待の有効期限</p>
                  <p className="text-sm text-dark">{expiresAt}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {error && invitation && (
          <div className="bg-destructive/10 rounded-lg px-4 py-3 flex items-start gap-2.5 mb-4">
            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
              パスワード
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="8文字以上"
                autoComplete="new-password"
                required
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'パスワードを非表示' : 'パスワードを表示'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {formErrors.password && <p className="text-destructive text-xs mt-1.5">{formErrors.password}</p>}
          </div>

          <div className="mb-6">
            <label htmlFor="passwordConfirmation" className="block text-sm font-medium text-gray-700 mb-1.5">
              パスワード（確認）
            </label>
            <div className="relative">
              <input
                id="passwordConfirmation"
                type={showConfirm ? 'text' : 'password'}
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                placeholder="パスワードを再入力"
                autoComplete="new-password"
                required
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                aria-label={showConfirm ? 'パスワードを非表示' : 'パスワードを表示'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {formErrors.passwordConfirmation && (
              <p className="text-destructive text-xs mt-1.5">{formErrors.passwordConfirmation}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary hover:bg-primary-hover text-white font-medium rounded-lg py-2.5 text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isLoading ? '処理中...' : '招待を承認'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default InviteAcceptPage;
